import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Text,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { resyncInboundTasks, taskKeys } from '../../services/complianceApi';
import type { InboundResyncResult, InboundResyncSkip } from '../../services/complianceApi';
import { stateTaxClientKeys } from '../../services/stateTaxClientsApi';
import { formatWholeDay } from '../../utils/complianceFormatters';

/**
 * Manual "re-check this client against the CRM" control, sitting in the filing-history area.
 *
 * The scheduled inbound sync (CRM task completed → statetax filing marked filed) is
 * watermark-driven: it only examines CRM tasks modified AFTER its last run, so a completion older
 * than the watermark is invisible to it permanently. That is not a bug that can be fixed by
 * waiting — it is why several May/June/July 2026 completions had to be backfilled by hand. This
 * button rescans ONE client's full completed-task history ignoring the watermark, and never moves
 * it, so a fleet-wide replay is not needed to repair one client that "looks wrong".
 *
 * The report is the feature. Pressing a button and being told nothing is the failure mode this
 * exists to remove, so every run ends with a plain-language sentence — including the boring case.
 * `filed: 0, alreadyFiled: 3` means "I checked, nothing was missing", which is REASSURANCE and is
 * rendered as success. Skips are listed one by one with their reason, never collapsed into a
 * count, because a skipped completion is a filing that is still wrong after the resync.
 *
 * Safe to press repeatedly: the server-side apply is idempotent on an already-filed filing and
 * always suppresses the client notification, so it can neither double-file nor email anyone.
 */

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
  },
  headRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  heading: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  controlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  detailLine: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  skips: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  skipsTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  skipsHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  skipRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:first-child': { borderTop: 'none' },
  },
  skipWhat: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  skipReason: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteDarkOrangeForeground1,
  },
  skipActivity: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
  },
});

/** Backend reason code → locale key. An unrecognised code renders raw rather than vanishing. */
const REASON_KEYS: Record<string, string> = {
  'no-confident-match': 'inboundResync.reason.noConfidentMatch',
  'error': 'inboundResync.reason.error',
  'not-allowlisted': 'inboundResync.reason.notAllowlisted',
};

type Outcome =
  | { kind: 'result'; result: InboundResyncResult }
  | { kind: 'disabled' }
  | { kind: 'notAllowlisted' }
  | { kind: 'failed'; message: string };

export function InboundResyncPanel({ stateTaxClientId }: { stateTaxClientId: string }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  async function handleResync() {
    if (busy) return;
    setBusy(true);
    // Clear the previous run's report immediately — a stale "all good" sitting above a running
    // re-check is worse than no message at all.
    setOutcome(null);
    try {
      const result = await resyncInboundTasks(stateTaxClientId);

      // Anything newly filed changes the filing feed, the filing-health roll-up and the task
      // list, so refresh all three rather than leaving the page contradicting the report.
      void qc.invalidateQueries({ queryKey: stateTaxClientKeys.all });
      void qc.invalidateQueries({ queryKey: taskKeys.all });

      setOutcome({ kind: 'result', result });
    } catch (err) {
      // Read status/code structurally: two ApiError classes exist in this app (one per API
      // module), so `instanceof` against either one is a coin flip waiting to misfire.
      const status = (err as { status?: number } | null)?.status;
      const code = (err as { code?: string } | null)?.code;
      if (status === 409 && code === 'inbound_sync_disabled') setOutcome({ kind: 'disabled' });
      else if (status === 409 && code === 'client_not_allowlisted') setOutcome({ kind: 'notAllowlisted' });
      else {
        setOutcome({
          kind: 'failed',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.headRow}>
        <Text className={styles.heading}>{t('inboundResync.title')}</Text>
        <div className={styles.controlRow}>
          <Button
            appearance="secondary"
            size="small"
            icon={<ArrowSyncRegular />}
            disabled={busy}
            onClick={() => { void handleResync(); }}
          >
            {t('inboundResync.button')}
          </Button>
          {busy && <Spinner size="tiny" label={t('inboundResync.running')} />}
        </div>
      </div>

      <Text className={styles.description} block>
        {t('inboundResync.description')}
      </Text>

      {outcome && <OutcomeReport outcome={outcome} />}
    </div>
  );
}

function OutcomeReport({ outcome }: { outcome: Outcome }) {
  const styles = useStyles();
  const { t } = useTranslation();

  if (outcome.kind === 'disabled') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>{t('inboundResync.disabled')}</MessageBarBody>
      </MessageBar>
    );
  }

  // Deliberately NOT an error intent. The inbound allowlist is still pinned to a single pilot
  // client, so this fires for almost everyone today — it describes how the system is configured,
  // not something the user got wrong.
  if (outcome.kind === 'notAllowlisted') {
    return (
      <MessageBar intent="info">
        <MessageBarBody>{t('inboundResync.notAllowlisted')}</MessageBarBody>
      </MessageBar>
    );
  }

  if (outcome.kind === 'failed') {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{t('inboundResync.failed', { error: outcome.message })}</MessageBarBody>
      </MessageBar>
    );
  }

  const { result } = outcome;
  const { examined, filed, alreadyFiled, skipped, skips } = result;

  // One headline sentence per distinct situation. "Nothing was missing" must not share copy with
  // "I filed things" — a user who cannot tell those apart cannot tell a working system from a
  // broken one, which is the whole reason this control exists.
  let headline: string;
  if (examined === 0) headline = t('inboundResync.summary.noCompletions');
  else if (filed > 0) headline = t('inboundResync.summary.filed', { filed, alreadyFiled });
  else if (alreadyFiled > 0) headline = t('inboundResync.summary.upToDate', { alreadyFiled });
  else headline = t('inboundResync.summary.noneApplied', { skipped });

  // Skips are the only part that needs a human, so they set the tone. Everything else — including
  // the very common filed:0/alreadyFiled:N — is a success.
  const intent = skipped > 0 ? 'warning' : 'success';

  return (
    <>
      <MessageBar intent={intent}>
        <MessageBarBody>{headline}</MessageBarBody>
      </MessageBar>

      <Text className={styles.detailLine} block>
        {t('inboundResync.summary.examined', { examined })}
      </Text>

      {skips.length > 0 && (
        <div className={styles.skips}>
          <Text className={styles.skipsTitle} block>
            {t('inboundResync.skipsTitle', { skipped: skips.length })}
          </Text>
          <Text className={styles.skipsHint} block>
            {t('inboundResync.skipsHint')}
          </Text>
          {skips.map((s) => (
            <SkipRow key={s.activityId} skip={s} />
          ))}
        </div>
      )}
    </>
  );
}

function SkipRow({ skip }: { skip: InboundResyncSkip }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const key = REASON_KEYS[skip.reason];

  return (
    <div className={styles.skipRow}>
      <Text className={styles.skipWhat} block>
        {skip.taxType} · {formatWholeDay(skip.periodStart)}
      </Text>
      {/* An unmapped code is shown verbatim — a new backend reason must still be readable here
          rather than rendering blank or leaking a missing-key string. */}
      <Text className={styles.skipReason} block>
        {key ? t(key) : skip.reason}
      </Text>
      <Text className={styles.skipActivity} block>
        {skip.activityId}
      </Text>
    </div>
  );
}
